<template>
    <div id="app">

        <!-- Header -->
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-icon"><span>🛡️</span></div>
                    <div>
                        <h1 class="dashboard-title">IOT Integrity Report</h1>
                        <div class="dashboard-subtitle">Daily fuel reconciliation - by account &amp; category</div>
                    </div>
                </div>
                <div class="header-controls">
                    <div class="live-indicator">
                        <div class="live-dot"></div>
                        <span style="color:white;font-size:0.875rem;">Live</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">

            <!-- Filters -->
            <div class="search-section">
                <div class="filter-header">
                    <h3 class="filter-title">Report Selection</h3>
                </div>
                <div class="search-controls-wrapper">
                    <div class="account-context">
                        <span class="account-context-label">Account</span>
                        <span class="account-context-value">{{ accountName || ('Account #' + accountId) }}</span>
                    </div>
                    <div class="search-controls">
                        <div class="search-group">
                            <label class="search-label">Date</label>
                            <input type="date" v-model="selectedDate" :max="maxDate" class="specific-date-input" />
                        </div>

                        <div class="search-group category-group">
                            <label class="search-label">Category</label>
                            <div class="category-tabs">
                                <button v-for="cat in categories" :key="cat.value"
                                    :class="['category-tab', { active: selectedCategory === cat.value }]"
                                    @click="selectedCategory = cat.value">
                                    <span class="category-icon">{{ cat.icon }}</span> {{ cat.label }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="filter-actions">
                        <button class="apply-filters-btn" :disabled="!canLoad" @click="loadReport(false)">
                            🔍 Load Report
                        </button>
                        <button class="regenerate-btn" :disabled="!canLoad" @click="loadReport(true)"
                            title="Recompute this report from the raw data, overwriting the cached version.">
                            {{ loading && regenerating ? '♻️ Regenerating…' : '♻️ Regenerate' }}
                        </button>
                        <button class="investigate-btn" :disabled="!canLoad || investigating" @click="investigateAccount">
                            {{ investigating ? '🤖 Investigating…' : '🤖 Investigate' }}
                        </button>
                        <span class="filter-hint" v-if="!accountId || !selectedDate">
                            Sign-in account/date required to continue.
                        </span>
                    </div>
                </div>
            </div>

            <!-- AI investigation result -->
            <div v-if="investigateError || investigateResult" class="grid-container investigate-panel">
                <div class="grid-header" @click="investigatePanelCollapsed = !investigatePanelCollapsed">
                    <div class="grid-title">
                        <span class="report-name">
                            <span class="collapse-chevron" :class="{ 'is-collapsed': investigatePanelCollapsed }">▾</span>
                            🤖 AI Investigation
                        </span>
                    </div>
                </div>
                <div class="report-body" v-show="!investigatePanelCollapsed">
                    <div v-if="investigateError" class="empty-state">
                        <div class="empty-icon">⚠️</div>
                        <div class="empty-title">Could not run the investigation</div>
                        <div class="empty-description">{{ investigateError }}</div>
                    </div>
                    <div v-else>
                        <p class="footnote">
                            Reviewed {{ investigateResult.builtFindingsCount }} finding(s) - volume mismatches, unexplained
                            transactions, possible fuel loss, communication failures, duplicate transactions, and device
                            errors/notifications - for {{ investigateResult.windowStart }} → {{ investigateResult.windowEnd }}.
                            {{ investigateResult.triageFailed ? 'Triage failed - nothing was investigated this run.' :
                                (investigateResult.investigatedCount + ' selected for deeper investigation' +
                                (investigateResult.skippedByCap ? ' (' + investigateResult.skippedByCap + ' more skipped by cap)' : '') + '.') }}
                        </p>

                        <div v-if="investigateResult.investigated.length" class="sub-section">
                            <div class="investigate-finding" v-for="(item, i) in investigateResult.investigated" :key="'inv' + i">
                                <div class="investigate-finding-header">
                                    <strong>{{ field(item.finding, 'category') }}</strong>
                                    <span class="footnote">{{ field(item.finding, 'site_name') }}</span>
                                </div>
                                <p class="narrative-text">{{ formatNarrative(item.root_cause_narrative) }}</p>
                            </div>
                        </div>
                        <div v-else class="no-findings-banner">
                            ✅ No findings needed deeper investigation this window.
                        </div>

                        <div class="investigate-links" v-if="investigateResult.pdfUrl || investigateResult.traceJsonUrl">
                            <a v-if="investigateResult.pdfUrl" :href="investigateResult.pdfUrl" target="_blank" rel="noopener" class="action-button export-btn">
                                <span>📄</span><span>Download Investigation PDF</span>
                            </a>
                            <a v-if="investigateResult.traceJsonUrl" :href="investigateResult.traceJsonUrl" target="_blank" rel="noopener" class="action-button export-btn">
                                <span>🧾</span><span>Raw Trace JSON</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Result -->
            <div v-if="loading" class="grid-container">
                <div class="report-body">
                    <div class="loading-state">
                        <div class="loader"></div>
                        <div class="loading-text">
                            Loading report… if this is the first request for this account/date it may take a
                            few seconds to generate on demand.
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="errorMessage" class="grid-container">
                <div class="report-body">
                    <div class="empty-state">
                        <div class="empty-icon">⚠️</div>
                        <div class="empty-title">Could not load report</div>
                        <div class="empty-description">{{ errorMessage }}</div>
                    </div>
                </div>
            </div>

            <div v-else-if="!report && !combinedSections" class="grid-container">
                <div class="report-body">
                    <div class="empty-state">
                        <div class="empty-icon">📋</div>
                        <div class="empty-title">No Report Loaded</div>
                        <div class="empty-description">Select an account, date, and category, then click <strong>Load
                                Report</strong>.</div>
                    </div>
                </div>
            </div>

            <!-- Single category -->
            <IOTReportSection v-else-if="activeSection" :section="activeSection" @row-click="openDetail" />

            <!-- Combined (all four categories) -->
            <div v-else-if="combinedSections" class="combined-stack">
                <div class="combined-pdf-bar" v-if="combinedReport && combinedReport.pdfUrl">
                    <a :href="combinedReport.pdfUrl" target="_blank" rel="noopener" class="action-button export-btn">
                        <span>📄</span><span>Download Combined PDF</span>
                    </a>
                </div>
                <IOTReportSection v-for="s in combinedSections" :key="s.category" :section="s"
                    @row-click="openDetail" />
            </div>

        </div>

        <!-- Row detail modal -->
        <div v-if="selectedDetail" class="detail-overlay" @click.self="selectedDetail = null">
            <div class="detail-modal">
                <div class="detail-modal-header">
                    <h4>{{ selectedDetail.title }}</h4>
                    <button class="detail-close-btn" @click="selectedDetail = null">✕</button>
                </div>
                <div class="detail-modal-body">
                    <div class="detail-field" v-for="f in selectedDetail.fields" :key="f[0]">
                        <span class="detail-field-label">{{ f[0] }}</span>
                        <span class="detail-field-value">{{ f[1] }}</span>
                    </div>
                    <div class="detail-note" v-if="selectedDetail.note">
                        <span class="detail-field-label">Why this was flagged</span>
                        <p>{{ selectedDetail.note }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import IOTReportSection from './IOTReportSection.vue';

export default {
    name: 'IOTAccountReport',

    components: { IOTReportSection },

    data: function () {
        return {
            loading: false,
            regenerating: false,
            errorMessage: '',
            report: null,
            combinedReport: null,
            selectedDetail: null,
            investigating: false,
            investigateResult: null,
            investigateError: '',
            investigatePanelCollapsed: false,
            accountId: null,
            accountName: '',
            selectedDate: '',
            selectedCategory: 'Usage',
            categories: [
                { value: 'Usage', label: 'Usage', icon: '⛽' },
                { value: 'Transfer', label: 'Transfer', icon: '🚚' },
                { value: 'Receiving', label: 'Receiving', icon: '📥' },
                { value: 'ATG', label: 'ATG', icon: '🛢️' },
                { value: 'Combined', label: 'Combined', icon: '📚' }
            ]
        };
    },

    mounted: function () {
        // The signed-in session is already scoped to one account (same
        // headers $ajaxGet attaches to every request) - no account picker
        // needed here, just read what's already stored at login.
        this.accountId = localStorage.getItem('userAccountId');
        this.accountName = localStorage.getItem('userAccount');
        var d = new Date();
        this.selectedDate = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    },

    computed: {
        maxDate: function () {
            return new Date().toISOString().split('T')[0];
        },

        canLoad: function () {
            return !!(this.accountId && this.selectedDate && this.selectedCategory && !this.loading);
        },

        activeSection: function () {
            if (this.selectedCategory === 'Combined' || !this.report) return null;
            return this.buildSection(this.selectedCategory, this.report);
        },

        combinedSections: function () {
            if (this.selectedCategory !== 'Combined' || !this.combinedReport) return null;
            return [
                this.buildSection('Usage', this.combinedReport.usage),
                this.buildSection('Transfer', this.combinedReport.transfer),
                this.buildSection('Receiving', this.combinedReport.receiving),
                this.buildSection('ATG', this.combinedReport.atg)
            ];
        }
    },

    methods: {
        // The AI narrative isn't guaranteed to come back with its own line
        // breaks before "•" bullets or "1)"/"2." numbered steps - the model
        // sometimes runs them together on one line. Force a break before each
        // marker so the text is always readable, regardless of how this
        // particular response happened to be formatted; combined with the
        // "narrative-text" class's white-space: pre-line, any \n the model
        // DID include is preserved too. Plain interpolation ({{ }}), not
        // v-html - no HTML is introduced, so this stays auto-escaped.
        formatNarrative: function (text) {
            if (!text) return 'No narrative returned for this finding.';
            return text
                .replace(/\r\n/g, '\n')
                .replace(/\s*•\s*/g, '\n• ')
                .replace(/([^\n])(\s)(\d{1,2}[.)]\s+)/g, '$1\n$3')
                .replace(/\n{3,}/g, '\n\n')
                .trim();
        },

        field: function (obj) {
            if (!obj) return null;
            for (var i = 1; i < arguments.length; i++) {
                if (obj[arguments[i]] !== undefined) return obj[arguments[i]];
            }
            return null;
        },

        // Normalizes one raw API row (as returned by either
        // IOTIntegrity/AccountReport or IOTIntegrityCombined/AccountReport)
        // into the shape the page/report template consumes - shared by both
        // the single-category and combined views so they can never drift.
        parseRow: function (raw) {
            if (!raw) return null;
            var rawJson = this.field(raw, 'reportJson', 'ReportJson');
            var detail = {};
            try { detail = rawJson ? JSON.parse(rawJson) : {}; } catch (e) { detail = {}; }

            return {
                accountName: this.field(raw, 'accountName', 'AccountName'),
                groupName: this.field(raw, 'groupName', 'GroupName'),
                windowStart: this.field(raw, 'windowStart', 'WindowStart'),
                windowEnd: this.field(raw, 'windowEnd', 'WindowEnd'),
                status: this.field(raw, 'status', 'Status'),
                comment: this.field(raw, 'comment', 'Comment'),
                pdfBlobName: this.field(raw, 'pdfBlobName', 'PdfBlobName'),
                reconciliationPct: this.field(raw, 'reconciliationPct', 'ReconciliationPct'),
                generatedAt: this.field(raw, 'generatedAt', 'GeneratedAt'),
                detail: detail
            };
        },

        // Derives everything the section template needs (KPI cards, item
        // arrays, "no findings" flag) for one category from its parsed
        // report - reused for the single-category view and, four times
        // over, for the Combined view.
        buildSection: function (categoryValue, parsedReport) {
            var cat = this.categories.find(function (c) { return c.value === categoryValue; });
            var detail = (parsedReport && parsedReport.detail) || {};
            var isSimpleCategory = categoryValue === 'Transfer' || categoryValue === 'Receiving';

            var kpiCards = [];
            if (categoryValue === 'Usage') {
                kpiCards = [
                    { label: 'Dispensed (L)', value: this.formatNum(detail.dispensed_l), color: '#1F2A44' },
                    { label: 'Reconciliation %', value: this.formatPct(detail.recon_pct), color: '#E8720C' },
                    { label: 'Investigate Items', value: detail.investigate_count != null ? detail.investigate_count : 0, color: detail.investigate_count ? '#C0392B' : '#1F7A43' }
                ];
            } else if (categoryValue === 'ATG') {
                kpiCards = [
                    { label: 'Possible Fuel Loss', value: detail.possible_loss_count != null ? detail.possible_loss_count : 0, color: detail.possible_loss_count ? '#C0392B' : '#1F7A43', hint: 'Confirmed unexplained tank declines' },
                    { label: 'Communication Failures', value: detail.comm_fail_count != null ? detail.comm_fail_count : 0, color: detail.comm_fail_count ? '#E8720C' : '#1F7A43', hint: 'Devices over the error tolerance' },
                    { label: 'Monitor Items', value: detail.monitor_count != null ? detail.monitor_count : 0, color: '#1F2A44', hint: 'Data-confidence flags, not confirmed events' }
                ];
            }

            var managementItems = detail.management_items || [];
            // Python's payload key is monitoring_atg/monitoring_comm with fields
            // {tank, window}/{device, ...}; the C# on-demand engine's is
            // atg_noise/comm_failures with {tank_name, window_start, window_end}/
            // {device_id, ...} - normalize both into one shape here so the table
            // and the row-detail modal work regardless of which engine wrote the row.
            var monitoringAtg = (detail.monitoring_atg || detail.atg_noise || []).map(function (m) {
                var windowStart = m.window_start;
                var windowEnd = m.window_end;
                if (windowStart == null && typeof m.window === 'string') {
                    var parts = m.window.split(' to ');
                    windowStart = parts[0];
                    windowEnd = parts[1];
                }
                return { tank: m.tank || m.tank_name || ('Tank ' + (m.tank_id != null ? m.tank_id : '?')), window_start: windowStart, window_end: windowEnd };
            });
            // C#'s raw comm_failures includes every status (Investigate items already
            // surface separately via management_items above) - Python's monitoring_comm
            // is pre-filtered to Monitor-only, so only apply the same filter when
            // falling back to the raw list, not when the pre-filtered field is present.
            var monitoringComm = (detail.monitoring_comm || (detail.comm_failures || []).filter(function (m) {
                return (m.status || '').toLowerCase() !== 'investigate';
            })).map(function (m) {
                return { device_id: m.device_id != null ? m.device_id : m.device, errors: m.errors, status: m.status, note: m.note };
            });
            var transactions = (detail.transactions || []).filter(function (t) {
                return t.classification && t.classification !== 'Reconciled';
            });

            // Python-authored payloads carry account-wide counts (investigate_count,
            // possible_loss_count, comm_fail_count, monitor_count) directly on the detail
            // object without necessarily populating this category's own item arrays (e.g. a
            // Usage record can carry investigate_count > 0 for a finding that belongs to the
            // ATG category) - treat any nonzero count as a finding too, not just the arrays.
            var hasCounts = (detail.investigate_count || 0) > 0 || (detail.possible_loss_count || 0) > 0 ||
                (detail.comm_fail_count || 0) > 0 || (detail.monitor_count || 0) > 0;
            var noFindings = !!parsedReport && parsedReport.status !== 'Failed' && !isSimpleCategory && !hasCounts &&
                !managementItems.length && !monitoringAtg.length && !monitoringComm.length && !transactions.length;

            return {
                category: categoryValue,
                label: cat ? cat.label : categoryValue,
                icon: cat ? cat.icon : '📊',
                report: parsedReport,
                detail: detail,
                isSimpleCategory: isSimpleCategory,
                kpiCards: kpiCards,
                managementItems: managementItems,
                monitoringAtg: monitoringAtg,
                monitoringComm: monitoringComm,
                transactions: transactions,
                noFindings: noFindings,
                statusBadgeClass: parsedReport && parsedReport.status === 'Failed' ? 'badge-failed' : 'badge-ok'
            };
        },

        loadReport: function (force) {
            var self = this;
            if (!this.canLoad) return;

            self.loading = true;
            self.regenerating = !!force;
            self.report = null;
            self.combinedReport = null;
            self.errorMessage = '';
            self.selectedDetail = null;

            if (this.selectedCategory === 'Combined') {
                var combinedUrl = 'IOTIntegrityCombined/AccountReport?accountId=' + encodeURIComponent(this.accountId) +
                    '&storeId=0&date=' + encodeURIComponent(this.selectedDate) +
                    (force ? '&force=true' : '');

                var onCombinedSuccess = function (response) {
                    var raw = response.data;
                    if (!raw) {
                        self.errorMessage = 'No data returned for this account/date.';
                        return;
                    }
                    self.combinedReport = {
                        usage: self.parseRow(self.field(raw, 'usage', 'Usage')),
                        transfer: self.parseRow(self.field(raw, 'transfer', 'Transfer')),
                        receiving: self.parseRow(self.field(raw, 'receiving', 'Receiving')),
                        atg: self.parseRow(self.field(raw, 'atg', 'Atg', 'ATG')),
                        pdfUrl: self.field(raw, 'combinedPdfUrl', 'CombinedPdfUrl')
                    };
                };
                // $ajaxGet has no onError callback - a failed request (e.g. this account
                // isn't enabled for IOT Integrity reporting) only shows a global toast, and
                // silently leaves combinedReport null here, which would otherwise render as
                // a misleading "No Report Loaded" rather than an explained error state.
                var onCombinedFinally = function () {
                    self.loading = false;
                    if (!self.combinedReport && !self.errorMessage) {
                        self.errorMessage = 'Could not load the combined report for this account/date - see the notification for details.';
                    }
                };
                self.$ajaxGet(self, combinedUrl, onCombinedSuccess, onCombinedFinally);
                return;
            }

            var url = 'IOTIntegrity/AccountReport?accountId=' + encodeURIComponent(this.accountId) +
                '&storeId=0&date=' + encodeURIComponent(this.selectedDate) +
                '&category=' + encodeURIComponent(this.selectedCategory) +
                (force ? '&force=true' : '');

            var onSuccess = function (response) {
                if (!response.data) {
                    self.errorMessage = 'No data returned for this account/date/category.';
                    return;
                }
                self.report = self.parseRow(response.data);
            };
            var onFinally = function () {
                self.loading = false;
                if (!self.report && !self.errorMessage) {
                    self.errorMessage = 'Could not load the report for this account/date/category - see the notification for details.';
                }
            };
            self.$ajaxGet(self, url, onSuccess, onFinally);
        },

        // Runs the on-demand AI agent (proxied through the .NET API so the
        // Python function key is never exposed to the browser) that reviews
        // this account+date's raw IOTData_Error/IOTData_Notification rows and
        // writes human-like root-cause observations - independent of which
        // report category tab is selected, since errors/notifications aren't
        // category-scoped.
        investigateAccount: function () {
            var self = this;
            if (!this.canLoad) return;

            self.investigating = true;
            self.investigateResult = null;
            self.investigateError = '';

            var url = 'IOTIntegrityInvestigate/AccountActivity?accountId=' + encodeURIComponent(this.accountId) +
                '&storeId=0&date=' + encodeURIComponent(this.selectedDate);

            var onSuccess = function (response) {
                var raw = response.data;
                if (!raw) {
                    self.investigateError = 'No data returned for this investigation.';
                    return;
                }
                self.investigateResult = {
                    builtFindingsCount: self.field(raw, 'builtFindingsCount', 'built_findings_count') || 0,
                    triageCount: self.field(raw, 'triageCount', 'triage_count') || 0,
                    investigatedCount: self.field(raw, 'investigatedCount', 'investigated_count') || 0,
                    skippedByCap: self.field(raw, 'skippedByCap', 'skipped_by_cap') || 0,
                    triageFailed: !!self.field(raw, 'triageFailed', 'triage_failed'),
                    pdfUrl: self.field(raw, 'pdfUrl', 'pdf_url'),
                    traceJsonUrl: self.field(raw, 'traceJsonUrl', 'trace_json_url'),
                    investigated: self.field(raw, 'investigated', 'Investigated') || [],
                    windowStart: self.formatWindowShort(self.field(raw, 'windowStart', 'window_start')),
                    windowEnd: self.formatWindowShort(self.field(raw, 'windowEnd', 'window_end'))
                };
            };
            var onFinally = function () {
                self.investigating = false;
                if (!self.investigateResult && !self.investigateError) {
                    self.investigateError = 'Could not run the investigation - see the notification for details.';
                }
            };
            self.$ajaxGet(self, url, onSuccess, onFinally);
        },

        formatWindowShort: function (v) {
            if (!v) return '—';
            var d = new Date(v);
            if (isNaN(d.getTime())) return v;
            var pad = function (n) { return String(n).padStart(2, '0'); };
            return d.getFullYear() + '/' + pad(d.getMonth() + 1) + '/' + pad(d.getDate());
        },

        // Builds the row-detail modal content from whichever table's row was
        // clicked (kind: 'transaction' | 'management' | 'atgNoise' | 'comm') -
        // every field the underlying JSON already carries (location, volume,
        // classification) plus the full reasoning note, not truncated as it
        // is in the table cell.
        openDetail: function (kind, row) {
            if (kind === 'transaction') {
                // Answers what an account manager actually asks when they see a flagged
                // row: "what did the till/pump log say?", "what did the raw device
                // (IOT/Android) actually report for this same transaction?", "how far
                // apart are they?" - the mismatch is always between the UsageDispensing
                // log entry (Logged Volume) and the matching UsageDispensingIOT/
                // UsageDispensingAndroid raw event for the same TransactionID (Raw ...
                // Volume), never a third source.
                var fields = [
                    ['Transaction ID', row.transaction_id],
                    ['Equipment ID', row.equipment_id],
                    ['Logged Volume (L) - till/pump', this.formatNum(row.volume)]
                ];
                if (row.matched_source) {
                    fields.push(['Compared Against', row.matched_source + ' telemetry (same Transaction ID)']);
                    fields.push(['Raw ' + row.matched_source + ' Volume (L)', this.formatNum(row.matched_volume)]);
                    var diff = (row.volume != null && row.matched_volume != null) ? Math.abs(row.volume - row.matched_volume) : null;
                    fields.push(['Difference (L)', diff != null ? this.formatNum(diff) : 'n/a']);
                } else {
                    fields.push(['Compared Against', 'No matching IOT or Android event found for this Transaction ID']);
                }
                fields.push(['Classification', row.classification]);

                this.selectedDetail = {
                    title: 'Transaction Detail',
                    fields: fields,
                    note: row.note
                };
            } else if (kind === 'management') {
                this.selectedDetail = {
                    title: row.category || 'Management Item',
                    fields: [['Affected', row.affected]],
                    note: row.note
                };
            } else if (kind === 'atgNoise') {
                this.selectedDetail = {
                    title: 'ATG Telemetry Noise - ' + row.tank,
                    fields: [['Window', row.window_start + ' → ' + row.window_end]],
                    note: 'Rolling-standard-deviation telemetry noise flag - not a confirmed physical event. ' +
                        'Investigate only if the same tank also appears under Possible Fuel Loss.'
                };
            } else if (kind === 'comm') {
                this.selectedDetail = {
                    title: 'Communication Errors - Device ' + row.device_id,
                    fields: [['Errors', row.errors], ['Status', row.status]],
                    note: row.note
                };
            }
        },

        formatNum: function (v) {
            if (v == null) return 'n/a';
            var n = parseFloat(v);
            return isNaN(n) ? v : n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        formatPct: function (v) {
            if (v == null) return 'n/a';
            var n = parseFloat(v);
            return isNaN(n) ? v : n.toFixed(1) + '%';
        }
    }
};
</script>

<style scoped>
* {
    box-sizing: border-box;
}

#app {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #F4F1EC;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

/* Header */
.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 18px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .2);
    border-bottom: 3px solid #E8720C;
    flex-shrink: 0;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
}

.logo-section {
    display: flex;
    align-items: center;
    gap: 14px;
}

.logo-icon {
    width: 44px;
    height: 44px;
    background: #E8720C;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
}

.dashboard-title {
    color: white;
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0;
}

.dashboard-subtitle {
    color: #cbd5e1;
    font-size: .8rem;
    margin-top: 2px;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.live-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, .12);
    padding: 8px 16px;
    border-radius: 20px;
}

.live-dot {
    width: 8px;
    height: 8px;
    background: #E8720C;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(232, 114, 12, .7); }
    70% { box-shadow: 0 0 0 10px rgba(232, 114, 12, 0); }
    100% { box-shadow: 0 0 0 0 rgba(232, 114, 12, 0); }
}

/* Main */
.main-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #F4F1EC;
}

/* Filters */
.search-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(31, 42, 68, .08);
    border: 1px solid #e5e1d8;
}

.filter-header {
    padding: 16px 20px;
    background: #F4F1EC;
    border-bottom: 1px solid #e5e1d8;
    border-radius: 12px 12px 0 0;
}

.filter-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1F2A44;
    margin: 0;
}

.search-controls-wrapper {
    padding: 20px;
}

.account-context {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
    padding: 10px 14px;
    background: #F4F1EC;
    border-left: 4px solid #1F2A44;
    border-radius: 6px;
}

.account-context-label {
    font-size: .78rem;
    color: #1F2A44;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .3px;
}

.account-context-value {
    font-size: .95rem;
    color: #2B2B2B;
    font-weight: 600;
}

.search-controls {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
}

.search-group {
    display: flex;
    flex-direction: column;
}

.category-group {
    grid-column: span 2;
}

.search-label {
    font-size: .8rem;
    color: #1F2A44;
    margin-bottom: 6px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .3px;
}

.specific-date-input {
    padding: 10px 12px;
    border: 1px solid #d6d0c4;
    border-radius: 8px;
    font-size: 14px;
    background: #fff;
    min-height: 44px;
}

.specific-date-input:focus {
    outline: none;
    border-color: #E8720C;
    box-shadow: 0 0 0 3px rgba(232, 114, 12, .15);
}

.category-tabs {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.category-tab {
    padding: 10px 16px;
    border: 1px solid #d6d0c4;
    background: #fff;
    border-radius: 8px;
    font-size: .875rem;
    font-weight: 600;
    color: #2B2B2B;
    cursor: pointer;
    transition: all .2s;
}

.category-tab:hover {
    border-color: #E8720C;
    background: #FDF3E9;
}

.category-tab.active {
    background: #E8720C;
    border-color: #E8720C;
    color: #fff;
}

.category-icon {
    margin-right: 4px;
}

.filter-actions {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-top: 12px;
    border-top: 1px solid #eee8db;
}

.apply-filters-btn {
    padding: 11px 26px;
    background: #E8720C;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 700;
    cursor: pointer;
    transition: all .2s;
    font-size: .9rem;
}

.apply-filters-btn:hover:not(:disabled) {
    background: #cf6409;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(232, 114, 12, .3);
}

.apply-filters-btn:disabled {
    background: #d6d0c4;
    cursor: not-allowed;
}

.regenerate-btn {
    padding: 11px 26px;
    background: white;
    color: #1F2A44;
    border: 2px solid #1F2A44;
    border-radius: 8px;
    font-weight: 700;
    cursor: pointer;
    transition: all .2s;
    font-size: .9rem;
}

.regenerate-btn:hover:not(:disabled) {
    background: #1F2A44;
    color: white;
    transform: translateY(-1px);
}

.regenerate-btn:disabled {
    background: #ede9de;
    color: #8a8478;
    border-color: #d6d0c4;
    cursor: not-allowed;
}

.filter-hint {
    font-size: .85rem;
    color: #8a8478;
    font-style: italic;
}

.investigate-btn {
    padding: 11px 26px;
    background: #1F2A44;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 700;
    cursor: pointer;
    transition: all .2s;
    font-size: .9rem;
}

.investigate-btn:hover:not(:disabled) {
    background: #16203a;
    transform: translateY(-1px);
}

.investigate-btn:disabled {
    background: #d6d0c4;
    cursor: not-allowed;
}

.investigate-panel {
    margin-bottom: 20px;
}

.investigate-panel .grid-header {
    padding: 16px 20px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    cursor: pointer;
    user-select: none;
}

.investigate-panel .grid-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.investigate-panel .report-name {
    font-size: 1.1rem;
    font-weight: 700;
    color: #fff;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.investigate-panel .collapse-chevron {
    display: inline-block;
    transition: transform .15s ease;
    font-size: .85em;
}

.investigate-panel .collapse-chevron.is-collapsed {
    transform: rotate(-90deg);
}

.investigate-finding {
    background: #F4F1EC;
    border-left: 4px solid #1F2A44;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 12px;
}

.investigate-finding-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 6px;
}

.investigate-finding-header strong {
    color: #1F2A44;
}

.investigate-finding p {
    margin: 0;
    color: #2B2B2B;
    font-size: .9rem;
    line-height: 1.5;
}

.investigate-finding p.narrative-text {
    white-space: pre-line;
    line-height: 1.7;
}

.investigate-links {
    display: flex;
    gap: 12px;
    margin-top: 16px;
    flex-wrap: wrap;
}

.sub-title {
    font-size: 1rem;
    font-weight: 700;
    color: #1F2A44;
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 2px solid #E8720C;
    display: inline-block;
}

.sub-section {
    margin-bottom: 24px;
}

.footnote {
    font-size: .78rem;
    color: #8a8478;
    font-style: italic;
    margin-top: 8px;
}

.no-findings-banner {
    background: #E3F3E8;
    color: #1F7A43;
    border-left: 4px solid #1F7A43;
    padding: 14px 18px;
    border-radius: 6px;
    font-weight: 600;
    text-align: center;
    margin-bottom: 20px;
}

.investigate-links .action-button {
    padding: 8px 16px;
    background: #E8720C;
    border: none;
    border-radius: 6px;
    color: white;
    font-size: .875rem;
    cursor: pointer;
    transition: all .2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    text-decoration: none;
}

.investigate-links .action-button:hover {
    background: #cf6409;
    transform: translateY(-1px);
}

/* Result containers (loading/error/empty states) */
.grid-container {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(31, 42, 68, .08);
    overflow: hidden;
    border: 1px solid #e5e1d8;
}

.report-body {
    padding: 24px;
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    gap: 16px;
}

.loader {
    width: 44px;
    height: 44px;
    border: 4px solid #F4F1EC;
    border-top-color: #E8720C;
    border-radius: 50%;
    animation: spin .7s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.loading-text {
    color: #6b6b6b;
    font-size: .9rem;
    text-align: center;
    max-width: 360px;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
}

.empty-icon {
    font-size: 2.5rem;
    margin-bottom: 12px;
}

.empty-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1F2A44;
    margin-bottom: 6px;
}

.empty-description {
    color: #8a8478;
    font-size: .9rem;
}

/* Combined view stack */
.combined-stack {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.combined-pdf-bar {
    display: flex;
    justify-content: flex-end;
}

.combined-pdf-bar .action-button {
    padding: 8px 16px;
    background: #E8720C;
    border: none;
    border-radius: 6px;
    color: white;
    font-size: .875rem;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    text-decoration: none;
    transition: all .2s;
}

.combined-pdf-bar .action-button:hover {
    background: #cf6409;
    transform: translateY(-1px);
}

/* Row detail modal */
.detail-overlay {
    position: fixed;
    inset: 0;
    background: rgba(31, 42, 68, .55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}

.detail-modal {
    background: #fff;
    border-radius: 12px;
    max-width: 520px;
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 12px 40px rgba(0, 0, 0, .3);
}

.detail-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: #1F2A44;
    border-radius: 12px 12px 0 0;
}

.detail-modal-header h4 {
    color: #fff;
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
}

.detail-close-btn {
    background: transparent;
    border: none;
    color: #fff;
    font-size: 1rem;
    cursor: pointer;
    padding: 4px 8px;
    line-height: 1;
}

.detail-close-btn:hover {
    color: #E8720C;
}

.detail-modal-body {
    padding: 20px;
}

.detail-field {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #eee8db;
    font-size: .9rem;
}

.detail-field-label {
    color: #8a8478;
    font-weight: 600;
    text-transform: uppercase;
    font-size: .75rem;
    letter-spacing: .3px;
    white-space: nowrap;
}

.detail-field-value {
    color: #2B2B2B;
    font-weight: 600;
    text-align: right;
}

.detail-note {
    margin-top: 16px;
}

.detail-note p {
    margin: 8px 0 0;
    color: #2B2B2B;
    font-size: .9rem;
    line-height: 1.5;
}
</style>
